import type { ImageAsset } from '@rive-app/react-canvas';
import { decodeImage } from '@rive-app/react-canvas';
import type { TFunction } from 'i18next';

import type { BonusItem } from '../../../../dto/bonus.types';
import type { LoyaltyTierKey } from '../../../../stores/loyalty/types';
import { isFreeSpinsBundleItem } from '../../../../stores/rewards/helpers';
import { formatValueWithK } from '../../../../utils/helpers';
import { devError } from '../../../../utils/log';
import { setString, setNumber, setBool, setEnum } from '../../../../utils/riveHelper';
import { translateTierName } from '../../../../utils/tierTranslation';
import { formatTime } from '../../../../utils/time';

import { EMPTY_PRIZE, EMPTY_STOP } from './constants';
import type { PrizeData, ProgressStop } from './dailyBonusRive.types';

// ---------------------------------------------------------------------------
// Prize helpers
// ---------------------------------------------------------------------------

export function hasGameContent(item: BonusItem): boolean {
  if (item.packageBundles && item.packageBundles.length > 0) {
    return item.packageBundles.some(isFreeSpinsBundleItem);
  }
  return item.gc_amount === 0 && item.sc_amount === 0 && !!item.image_url;
}

function pickGameContentImageUrl(item: BonusItem): string | undefined {
  if (item.packageBundles && item.packageBundles.length > 0) {
    const gameBundle = item.packageBundles.find(isFreeSpinsBundleItem);
    const payload = gameBundle?.bundlePayload as { gameImageUrl?: string; imageUrl?: string } | undefined;
    return payload?.gameImageUrl ?? payload?.imageUrl ?? item.image_url;
  }
  return item.image_url;
}

export function getMaxSlotsForDay(item: BonusItem, lastDay: number): number {
  return item.day === lastDay ? 3 : 2;
}

export function collectPrizes(
  item: BonusItem,
  isScModeAvailable: boolean,
  t: TFunction,
  maxSlots: number = Number.POSITIVE_INFINITY,
): PrizeData[] {
  type DraftPrize = Omit<PrizeData, 'placeholderNum'>;
  const drafts: DraftPrize[] = [];

  if (item.gc_amount > 0) {
    drafts.push({ ...EMPTY_PRIZE, prizeType: 'CC', coinsAmount: formatValueWithK(item.gc_amount, t) });
  }

  if (item.sc_amount > 0 && isScModeAvailable) {
    drafts.push({ ...EMPTY_PRIZE, prizeType: 'SC', coinsAmount: formatValueWithK(item.sc_amount, t) });
  }

  if (hasGameContent(item)) {
    drafts.push({ ...EMPTY_PRIZE, prizeType: 'MINI_GAME', imageUrl: pickGameContentImageUrl(item) });
  }

  if (!item.packageBundles && item.image_url_3) {
    drafts.push({ ...EMPTY_PRIZE, prizeType: 'MINI_GAME', imageUrl: item.image_url_3 });
  }

  // Priority when capacity is exceeded: GC > MINI_GAME > SC. Production data is expected
  // to set only one coin per day, but if both are present we drop SC so the minigame survives.
  while (drafts.length > maxSlots) {
    const scIdx = drafts.findIndex((p) => p.prizeType === 'SC');
    if (scIdx >= 0) {
      drafts.splice(scIdx, 1);
    } else {
      drafts.pop();
    }
  }

  return drafts.map((p, i) => ({ ...p, placeholderNum: `day_${item.day}_prize_${i + 1}` }));
}

function bindPrizeSlots(dayVM: any, prizes: PrizeData[], maxSlots: number) {
  for (let i = 0; i < maxSlots; i++) {
    const slotVM = dayVM?.viewModel?.(`prize${i + 1}`);
    if (!slotVM) continue;

    const prize = prizes[i] ?? EMPTY_PRIZE;
    setEnum(slotVM, 'prizeType', prize.prizeType);
    setString(slotVM, 'coinsAmount', prize.coinsAmount);
  }
}

// ---------------------------------------------------------------------------
// Day card binders
// ---------------------------------------------------------------------------

function bindDayCommon(
  dayVM: any,
  item: BonusItem,
  currentDay: number,
  maxSlots: number,
  isScModeAvailable: boolean,
  t: TFunction,
) {
  setString(dayVM, 'header', t('bonus.day', { day: item.day }));
  setBool(dayVM, 'isCurrentDay', currentDay === item.day);
  setBool(dayVM, 'isClaimed', currentDay >= item.day);

  const prizes = collectPrizes(item, isScModeAvailable, t, maxSlots);
  setNumber(dayVM, 'numOfPrizes', prizes.length);
  bindPrizeSlots(dayVM, prizes, maxSlots);
}

export function bindDayCards(
  popupVM: any,
  dailyBonuses: BonusItem[],
  currentDay: number,
  isScModeAvailable: boolean,
  t: TFunction,
) {
  if (!dailyBonuses.length) return;

  const lastDay = Math.max(...dailyBonuses.map((b) => b.day));

  for (const item of dailyBonuses) {
    const dayVM = popupVM?.viewModel?.(`day${item.day}`);
    if (!dayVM) continue;

    bindDayCommon(dayVM, item, currentDay, getMaxSlotsForDay(item, lastDay), isScModeAvailable, t);
  }
}

// ---------------------------------------------------------------------------
// Top banner (monthly progress)
// ---------------------------------------------------------------------------

function buildProgressStops(monthlyBonusDay: number, milestoneDays: number[]): ProgressStop[] {
  return [
    { num: monthlyBonusDay, isCurrent: true, isChecked: false },
    ...milestoneDays.map((milestone) => ({
      num: milestone,
      isCurrent: monthlyBonusDay === milestone,
      isChecked: monthlyBonusDay >= milestone,
    })),
  ];
}

// The Rive file exposes placeholder_1..placeholder_16 image-asset slots — a flat grid across the
// 4 monthly tooltips × 4 prize slots. The view-model property `placeholderNum` is the global index
// Rive uses to pick which placeholder_N to render for a given (tooltipIndex, prizeSlot) pair.
const TOOLTIP_PRIZE_SLOTS = 4;

function globalPlaceholderIndex(tooltipIndex: number, prizeSlot: number): number {
  return tooltipIndex * TOOLTIP_PRIZE_SLOTS + prizeSlot + 1;
}

function ShowToolTipPrizes(
  item: BonusItem,
  isScModeAvailable: boolean,
  t: TFunction,
  tooltipIndex: number,
): PrizeData[] {
  const prizes: PrizeData[] = [];
  let slot = 0;

  if (item.gc_amount > 0) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'CC',
      placeholderNum: String(globalPlaceholderIndex(tooltipIndex, slot)),
      coinsAmount: formatValueWithK(item.gc_amount, t),
    });
    slot++;
  }

  if (item.sc_amount > 0 && isScModeAvailable) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'SC',
      placeholderNum: String(globalPlaceholderIndex(tooltipIndex, slot)),
      coinsAmount: formatValueWithK(item.sc_amount, t),
    });
    slot++;
  }

  if (hasGameContent(item)) {
    prizes.push({
      ...EMPTY_PRIZE,
      prizeType: 'MINI_GAME',
      placeholderNum: String(globalPlaceholderIndex(tooltipIndex, slot)),
      imageUrl: pickGameContentImageUrl(item),
    });
    slot++;
  }

  return prizes;
}

export function bindMonthlyToolTips(
  rootVM: any,
  monthlyBonuses: BonusItem[],
  monthlyBonusDay: number,
  isScModeAvailable: boolean,
  t: TFunction,
) {
  const sorted = [...monthlyBonuses].sort((a, b) => a.day - b.day);

  for (let i = 0; i < sorted.length; i++) {
    const toolTipVM = rootVM?.viewModel?.(`toolTip${i + 1}`);
    if (!toolTipVM) continue;

    const item = sorted[i];
    const isClaimed = monthlyBonusDay >= item.day;

    const prizes = ShowToolTipPrizes(item, isScModeAvailable, t, i);
    setNumber(toolTipVM, 'numOfPrizes', prizes.length);

    for (let j = 0; j < TOOLTIP_PRIZE_SLOTS; j++) {
      const prizeVM = toolTipVM?.viewModel?.(`prize${j + 1}`);
      if (!prizeVM) continue;

      const prize = prizes[j];
      if (prize) {
        setEnum(prizeVM, 'prizeType', prize.prizeType);
        setString(prizeVM, 'coinsAmount', prize.coinsAmount);
        setNumber(prizeVM, 'placeholderNum', Number(prize.placeholderNum));
      }
      setBool(prizeVM, 'prizeChecked', isClaimed);
    }
  }
}

function computeProgress(day: number, milestoneDays: number[]): number {
  if (day <= 0 || milestoneDays.length === 0) return 0;

  const stops = [0, ...milestoneDays];
  const sliceSize = 100 / milestoneDays.length;
  const lastStop = stops[stops.length - 1];
  if (day >= lastStop) return 100;

  for (let i = 1; i < stops.length; i++) {
    if (day === stops[i]) return i * sliceSize;
    if (day < stops[i]) {
      const segStart = stops[i - 1];
      const segEnd = stops[i];
      const segProgress = (day - segStart) / (segEnd - segStart);
      return (i - 1) * sliceSize + segProgress * sliceSize;
    }
  }
  return 0;
}

export function bindTopBanner(
  popupVM: any,
  isEntitledToBonus: boolean,
  monthlyBonusDay: number,
  monthlyBonuses: BonusItem[],
  tierKey: LoyaltyTierKey,
) {
  const topBannerVM = popupVM?.viewModel?.('topBanner');
  if (!topBannerVM) return;

  const milestoneDays = monthlyBonuses.map((b) => b.day).sort((a, b) => a - b);
  const progress = computeProgress(
    monthlyBonusDay,
    milestoneDays.map((d) => d - 1),
  );

  setNumber(topBannerVM, 'progBar', Math.round(progress));
  setString(topBannerVM, 'tier', translateTierName(tierKey));
  setBool(topBannerVM, 'isEntitledToBonus', isEntitledToBonus);

  const stops = buildProgressStops(monthlyBonusDay, milestoneDays);

  for (let i = 0; ; i++) {
    const progVM = topBannerVM?.viewModel?.(`prog${i + 1}`);
    if (!progVM) break;

    const stop = stops[i] ?? EMPTY_STOP;
    setNumber(progVM, 'num', stop.num);
    setBool(progVM, 'isCurrent', stop.isCurrent);
    setBool(progVM, 'isChecked', stop.isChecked);
  }
}

// ---------------------------------------------------------------------------
// Countdown
// ---------------------------------------------------------------------------

export function bindCountdown(parentVM: any, timeUntilNextBonusMS: number) {
  const targetVM = parentVM;
  if (!targetVM) return;

  setString(targetVM, 'time', formatTime(timeUntilNextBonusMS, 'ms'));
}

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export async function loadRiveImage(url: string, asset: ImageAsset) {
  try {
    const res = await fetch(url);
    const image = await decodeImage(new Uint8Array(await res.arrayBuffer()));
    asset.setRenderImage(image);
    image.unref();
  } catch (e) {
    devError('Failed to load Rive asset image', e);
  }
}

// ---------------------------------------------------------------------------
// Referenced assets — maps placeholder names to image URLs for asset loading
// ---------------------------------------------------------------------------

export function buildReferencedAssets(
  dailyBonuses: BonusItem[],
  monthlyBonuses: BonusItem[],
  t: TFunction,
): Record<string, string> {
  const assets: Record<string, string> = {};

  if (dailyBonuses.length) {
    const lastDay = Math.max(...dailyBonuses.map((b) => b.day));
    for (const item of dailyBonuses) {
      // The MINI_GAME slot's placeholder name depends on whether SC mode is on
      // (CC → SC? → MINI_GAME shifts the index). Register both possible slot positions
      // so the correct asset resolves regardless of the user's mode at bind time.
      for (const isScModeAvailable of [true, false]) {
        const prizes = collectPrizes(item, isScModeAvailable, t, getMaxSlotsForDay(item, lastDay));
        for (const prize of prizes) {
          if (prize.imageUrl) {
            assets[prize.placeholderNum] = prize.imageUrl;
          }
        }
      }
    }
  }

  // Monthly tooltips: the Rive file exposes tooltip_1..tooltip_4 (and _claimed variants) as
  // per-tooltip image slots, plus placeholder_1..placeholder_16 as a flat (tooltipIdx, slot)
  // grid indexed by `placeholderNum`. Register both forms so whichever the Rive file uses
  // for the minigame icon picks up the URL. The placeholder_N entries are derived by calling
  // ShowToolTipPrizes for both SC modes — single source of truth for slot ordering, so any
  // future reshuffle of CC/SC/MINI_GAME emission order stays in sync automatically.
  const sortedMonths = [...monthlyBonuses].sort((a, b) => a.day - b.day);
  for (let i = 0; i < Math.min(sortedMonths.length, TOOLTIP_PRIZE_SLOTS); i++) {
    const item = sortedMonths[i];
    const tooltipImageUrl = pickGameContentImageUrl(item);

    if (tooltipImageUrl) {
      assets[`tooltip_${i + 1}`] = tooltipImageUrl;
      assets[`tooltip_${i + 1}_claimed`] = tooltipImageUrl;
    }

    for (const isScModeAvailable of [true, false]) {
      for (const prize of ShowToolTipPrizes(item, isScModeAvailable, t, i)) {
        if (prize.imageUrl) {
          assets[`placeholder_${prize.placeholderNum}`] = prize.imageUrl;
        }
      }
    }
  }

  return assets;
}
